import { Flex } from '@cognite/cogs.js-v10';
import { LOCIZE_NAMESPACES, useTranslation } from '@infield/features/i18n';
import { BREAKPOINT_PAD_PORTRAIT } from '@infield/providers/theme';
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';

import * as S from './elements';
import { splitEmptyValues } from './utils';

type Props = {
  items: AssetPropertiesListItem[];
};

export type AssetPropertiesListItem = {
  fieldName: string;
  fieldValue: string;
  isHeader?: boolean;
  onClickName?: () => unknown;
  onClickValue?: () => unknown;
};

export const AssetPropertiesList: FC<Props> = ({ items }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number | undefined>(
    undefined
  );
  const [showEmpty, setShowEmpty] = useState<boolean>(false);
  const { t } = useTranslation(LOCIZE_NAMESPACES.asset);

  const buttonText = showEmpty
    ? t('ASSET_PROPERTIES_HIDE_EMPTY_FIELDS', 'Hide empty fields')
    : t('ASSET_PROPERTIES_VIEW_EMPTY_FIELDS', 'View empty fields');

  useEffect(() => {
    setContainerWidth(containerRef.current?.offsetWidth);
  }, [containerRef.current?.offsetWidth]);

  const [withValues, withoutValues] = splitEmptyValues(items);

  const isFullWidth =
    containerWidth !== undefined && containerWidth > BREAKPOINT_PAD_PORTRAIT;

  const row = ({
    fieldName,
    fieldValue,
    isHeader,
    onClickName,
    onClickValue,
  }: AssetPropertiesListItem) => {
    if (isHeader && !isFullWidth) {
      return;
    }
    return (
      <S.Row key={fieldName} $containerWidth={containerWidth}>
        <S.LeftColumn $containerWidth={containerWidth}>
          <S.ListItem
            strong={isHeader || !isFullWidth}
            $containerWidth={containerWidth}
            $isHeader={isHeader || !isFullWidth}
            $isClickable={onClickName !== undefined}
            onClick={onClickName}
          >
            {fieldName}
          </S.ListItem>
        </S.LeftColumn>
        <S.RightColumn>
          <S.ListItem
            strong={isHeader}
            $containerWidth={containerWidth}
            $isHeader={isHeader}
            $isClickable={onClickValue !== undefined}
            onClick={onClickValue}
          >
            {fieldValue}
          </S.ListItem>
        </S.RightColumn>
      </S.Row>
    );
  };

  return (
    <Flex direction="column" fullWidth>
      <S.List ref={containerRef}>
        {containerWidth ? withValues.map((listItem) => row(listItem)) : null}
        {showEmpty && (
          <>
            {containerWidth
              ? withoutValues.map((listItem) => row(listItem))
              : null}
          </>
        )}
      </S.List>
      {withoutValues && withoutValues.length > 0 && (
        <S.EmptyFieldButton
          size="large"
          onClick={() => setShowEmpty((prev) => !prev)}
        >
          {buttonText}
        </S.EmptyFieldButton>
      )}
    </Flex>
  );
};
